import React, { useEffect, useState } from 'react';
import { Route, Routes, Navigate, useLocation, matchPath } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { store, persistor } from './app/services/store';
import {
  VerifyEmailScreen, VerificationCodeScreen, VerifyDOBScreen, SetPasswordScreen,
  LoginScreen, HomeScreen, ParentalDetailsScreen, BasicDetailsScreen,
  ObservationDetailScreen, InvoiceScreen, TermsAndConditionsScreen, ActivitiesScreen,
  OptionsScreen, Eyengage, EyengagePostDetailsScreen, WebView, Resources,
  Planning, AccidentSheetDetails, MedicineFormDetails, CreateUpdateMedicineForm, TransitionReport,
  UnsubscribeEmailScreen, DailyDiaryDetailsScreen, DeepLinkScreen, NewDownloadAppScreen,
  DownloadAppScreen, NotificationsPage, DocumentViewer, SummativeReport, ValidateOrder,
  OrderErrorScreen, DownloadLinkScreen, PdfBookContainer, CommunicationDetails,
  CommunicationReply, CommunicationsContainer, ConversationsContainer, EylogMessageContainer,
  Eymemories, StripeHandling
} from './app/pages'
import { AppRoutes, EymemoryRoutes, EymemoryRoutesList, PublicRoutesList } from './app/navigation/routes';
import { AuthGuard, EymemoryAuthGuard, LoginStepperGuard, TermsConditionsGuard } from './app/guards';
import AddObservation from './app/pages/observations/AddObservation';
import { FullScreenLoader } from './app/components';
import { PersistGate } from 'redux-persist/integration/react';
import { usePreviousRoute } from 'src/app/guards/PreviousRouteContext';
import ForbiddenScreen from './app/pages/eymemory-orders/ForbiddenScreen';
import { CAPCHA_SITE_KEY } from "src/app/config/appConstants";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import useDeviceId from './app/utilities/hooks/useDeviceId';
import { useAppInitializer, useEymemoryAuth } from './app/hooks';

const App = () => {
  const location = useLocation();
  const deviceId = useDeviceId();

  const {previousRoute, emailSetter} = usePreviousRoute();
  const [validatingTokens, setValidatingTokens] = useState(true);

  const { initializeApp } = useAppInitializer({
    deviceId,
    previousRoute,
    emailSetter,
    setValidatingTokens,
    store,
  });

  const { initializeEymemorySession } = useEymemoryAuth({
    store,
    setValidatingTokens,
  });

  const isPublicRoute = PublicRoutesList.some((route) =>
    matchPath(route, location.pathname)
  );

  const isEymemoryRoute = EymemoryRoutesList.some((route) =>
    matchPath(route, location.pathname)
  );

  // Non Eymemory session handling
  useEffect(() => {
    if (isPublicRoute) {
      setValidatingTokens(false);
      return;
    }
    if (isEymemoryRoute) return;
    if (deviceId) {
      try {
        (async () => await initializeApp())();
      } catch (error) {
        console.error("Error while validating session", error);
      } finally {
        setValidatingTokens(false);
      }
    }
  }, [deviceId]);

  // Eymemory session handling
  useEffect(() => {
    if (isPublicRoute) {
      setValidatingTokens(false);
      return;
    }
    if (!isEymemoryRoute) return;
    try {
      (async () => await initializeEymemorySession())();
    } catch (error) {
      console.error("Error while validating eymemory session", error);
    } finally {
      setValidatingTokens(false);
    }
  }, []);


  return (
    <>
      {validatingTokens ? (
        <div className={`main-wrapper bg-white container`}>
          <FullScreenLoader />
        </div>
      ) : (
        <Routes>
          <Route
            path={AppRoutes.MOBILE_APP_LANDSCAPE_VIEW}
            element={<NewDownloadAppScreen />}
          />
          <Route
            path={EymemoryRoutes.EYMEMORIES_ERROR}
            element={<OrderErrorScreen />}
          />
          <Route
            path={EymemoryRoutes.EYMEMORIES_FORBIDDEN}
            element={<ForbiddenScreen />}
          />
          <Route
            path={EymemoryRoutes.VALIDATE_EYMEMORIES_DOWNLOAD_LINK}
            element={
              <GoogleReCaptchaProvider reCaptchaKey={CAPCHA_SITE_KEY}>
                <ValidateOrder />
              </GoogleReCaptchaProvider>
            }
          />
          <Route element={<EymemoryAuthGuard />}>
            <Route
              path={`${EymemoryRoutes.EYMEMORIES_DOWNLOAD_FROM_LINK}/:orderHash`}
              element={<DownloadLinkScreen />}
            />
          </Route>
          <Route element={<LoginStepperGuard />}>
            <Route
              path={AppRoutes.VERIFY_EMAIL}
              element={<VerifyEmailScreen />}
            />
            <Route path={AppRoutes.LOGIN} element={<LoginScreen />} />
            <Route
              path={AppRoutes.VERIFY_DOB}
              element={<VerifyDOBScreen />}
            />
            <Route
              path={AppRoutes.VERIFY_OTP}
              element={<VerificationCodeScreen />}
            />
            <Route
              path={AppRoutes.SET_PASSWORD}
              element={<SetPasswordScreen />}
            />
          </Route>
          <Route element={<AuthGuard />}>
            <Route
              path={AppRoutes.NOTIFICATIONS}
              element={<NotificationsPage />}
            />
            <Route path={AppRoutes.HOME} element={<HomeScreen />} />
            <Route path={AppRoutes.EYENGAGE} element={<Eyengage />} />
            <Route
              path={`${AppRoutes.EYENGAGE_POST_DETAILS}/:postId`}
              element={<EyengagePostDetailsScreen />}
            />
            <Route element={<TermsConditionsGuard />}>
              <Route
                path={AppRoutes.TERMS_CONDITIONS}
                element={<TermsAndConditionsScreen />}
              />
            </Route>
            <Route path={AppRoutes.TIMELINE} element={<ActivitiesScreen />} />
            <Route path={`${AppRoutes.LEARNING_JOURNEY_PRINT}`} element={<PdfBookContainer />} />
            <Route path={AppRoutes.OPTIONS} element={<OptionsScreen />} />
            <Route
              path={AppRoutes.BASIC_PROFILE}
              element={<BasicDetailsScreen />}
            />
            <Route
              path={AppRoutes.PARENTAL_PROFILE}
              element={<ParentalDetailsScreen />}
            />
            <Route
              path={AppRoutes.OBSERVATION_DETAILS_SCREEN(":id")}
              element={<ObservationDetailScreen />}
            />
            <Route
              path={AppRoutes.ADD_OBSERVATION}
              element={<AddObservation />}
            />
            <Route path={AppRoutes.INVOICE} element={<InvoiceScreen />} />
            <Route path={AppRoutes.INVOICE_DETAILS} element={<WebView />} />
            <Route path={AppRoutes.PLANNING} element={<Planning />} />
            <Route
              path={AppRoutes.RESOURCES_DOCUMENTS}
              element={<Resources />}
            />
            <Route path={AppRoutes.RESOURCES_URL} element={<WebView />} />
            <Route
              path={AppRoutes.ACCIDENT_SHEET_DETAILS_SCREEN(":id")}
              element={<AccidentSheetDetails />}
            />
            <Route
              path={AppRoutes.MEDICINE_FORM_DETAILS(":id")}
              element={<MedicineFormDetails />}
            />
            <Route
              path={AppRoutes.CREATE_UPDATE_MEDICINE_FORM(":id")}
              element={<CreateUpdateMedicineForm />}
            />
            <Route path={AppRoutes.BOOKINGS} element={<WebView />} />
            <Route
              path={`${AppRoutes.EYMEMORIES_PAYMENT_SUCCESS}/:sessionId`}
              element={<StripeHandling isSuccessPage={true} />}
            />
            <Route
              path={`${AppRoutes.EYMEMORIES_PAYMENT_FAILURE}/:sessionId`}
              element={<StripeHandling isSuccessPage={false} />}
            />
            <Route path={`${AppRoutes.EYMEMORIES}/*`} element={<Eymemories />} />
            <Route
              path={AppRoutes.CONVERSATIONS}
              element={<ConversationsContainer />}
            />
            <Route
              path={AppRoutes.COMMUNICATIONS}
              element={<CommunicationsContainer />}
            />
            <Route
              path={`${AppRoutes.COMMUNICATIONS}/:communicationId`}
              element={<CommunicationDetails />}
            />
            <Route
              path={`${AppRoutes.COMMUNICATIONS}/:communicationId${AppRoutes.REPLY}`}
              element={<CommunicationReply />}
            />
            <Route
              path={AppRoutes.EYLOG_MESSAGES}
              element={<EylogMessageContainer />}
            />
            <Route
              path={AppRoutes.TRANSITION_REPORT_DETAILS}
              element={<TransitionReport />}
            />
            <Route
              path={AppRoutes.SUMMATIVE_REPORT_DETAILS}
              element={<SummativeReport />}
            />
            <Route
              path={AppRoutes.CUSTOM_TRANSITION_REPORT}
              element={<WebView />}
            />
            <Route
              path={AppRoutes.CUSTOM_SUMMATIVE_REPORT}
              element={<WebView />}
            />
            <Route
              path={AppRoutes.MOBILE_APP}
              element={<DownloadAppScreen />}
            />
            <Route
              path={AppRoutes.NOTIFICATIONS}
              element={<NotificationsPage />}
            />
            <Route
              path={AppRoutes.UNSUBSCRIBE_EMAILS}
              element={<UnsubscribeEmailScreen />}
            />
            <Route
              path={AppRoutes.NOTIFICATIONS_SETTINGS}
              element={<UnsubscribeEmailScreen />}
            />
            <Route
              path={AppRoutes.DAILY_DIARY_SCREEN}
              element={<DailyDiaryDetailsScreen />}
            />
            <Route path={AppRoutes.DEEP_LINK} element={<DeepLinkScreen />} />
            <Route
              path={AppRoutes.DOCUMENT_VIEWER}
              element={<DocumentViewer />}
            />
          </Route>
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
      <ToastContainer newestOnTop={true} autoClose={3000} />
    </>
  );
}

const Main = () => {
  return (
      <ReduxProvider store={store} stabilityCheck='never'>
        <PersistGate loading={<FullScreenLoader />} persistor={persistor}>
          <App />
        </PersistGate>
      </ReduxProvider>
  )
}

export default Main;
